import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";

const columns = [
  { key: "Codigo", label: "Código" },
  { key: "Clientes", label: "Cliente" },
  { key: "Monto", label: "Monto" },
  { key: "Fecha", label: "Fecha" },
  { key: "Vencimiento", label: "Vencimiento" },
  { key: "Unidad", label: "Unidad" },
];

const currencyToIntl = (moneda) => {
  const m = String(moneda || "").trim().toUpperCase();
  if (m === "USD" || m === "US$" || m === "$") return "USD";
  if (m === "GS" || m === "PYG" || m === "₲" || m === "G" || m === "GS.") return "PYG";
  return null;
};

const formatMoney = (value, moneda) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  const currency = currencyToIntl(moneda);
  if (!currency) {
    return `${num.toLocaleString("es-PY")} ${moneda || ""}`.trim();
  }

  return new Intl.NumberFormat("es-PY", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "USD" ? 2 : 0,
  }).format(num);
};

const GestionInmobiliariaContratos = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const title = "Contratos";

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
        const companyDb = user?.CompanyDB;
        const res = await apiClient.get("/inmobiliaria/contratos", {
          params: { CompanyDB: companyDb },
        });
        if (!alive) return;
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.message || "Error cargando contratos");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []);

  const hasData = useMemo(() => rows && rows.length > 0, [rows]);

  const filteredRows = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return rows;
    return (rows || []).filter((r) => {
      return columns.some((c) => {
        const v = r?.[c.key];
        return String(v ?? "").toLowerCase().includes(q);
      });
    });
  }, [rows, search]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const onGestionar = (row) => {
    const codigo = row?.Codigo;
    if (codigo === null || codigo === undefined || String(codigo).trim() === "") return;
    navigate(`/inmobiliaria/contratos/${codigo}`);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">{title}</h5>
          <small className="text-muted">Listado de registros</small>
        </div>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => window.location.reload()}
          type="button"
        >
          Recargar
        </button>
      </div>

      <div className="card-body">
        {loading && <div className="alert alert-info mb-0">Cargando...</div>}
        {!loading && error && <div className="alert alert-danger mb-0">{error}</div>}

        {!loading && !error && (
          <>
            <div className="row g-2 align-items-end mb-3">
              <div className="col-12 col-md-6">
                <label className="form-label mb-1">Buscar</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="Buscar por cualquier campo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label mb-1">Registros por página</label>
                <select
                  className="form-select form-select-sm"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-3 text-md-end">
                <div className="small text-muted">
                  Mostrando {total === 0 ? 0 : (page - 1) * pageSize + 1}-
                  {Math.min(page * pageSize, total)} de {total}
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
              <thead className="table-primary">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                  <th style={{ width: 140 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} className="text-center text-muted py-4">
                      Sin registros
                    </td>
                  </tr>
                )}

                {filteredRows.length > 0 &&
                  paginatedRows.map((row, idx) => (
                    <tr key={`${row?.Codigo ?? "row"}-${idx}`}>
                      {columns.map((c) => {
                        const value = row?.[c.key];
                        const rendered = c.key === "Monto" ? formatMoney(value, row?.Moneda) : value;
                        return <td key={c.key}>{rendered ?? ""}</td>;
                      })}
                      <td>
                        <button
                          className="btn btn-sm btn-success"
                          type="button"
                          onClick={() => onGestionar(row)}
                        >
                          Gestionar
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

            <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-center mt-3">
              <div className="small text-muted">Página {page} de {totalPages}</div>
              <div className="btn-group" role="group" aria-label="Paginación">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                >
                  «
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Anterior
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Siguiente
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                >
                  »
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GestionInmobiliariaContratos;
